import type { JSX } from "react";
import { useRef, useState } from "react";
import { cn } from "@openreel/ui";

export interface CodeInputProps {
  value: string;
  onChange: (next: string) => void;
  length: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  "aria-label": string;
}

/**
 * Six boxes, one input.
 *
 * The boxes are painted, not typed into: a single real <input> is stretched
 * across them, transparent, and the digits are rendered underneath. That is
 * deliberate. Six separate inputs is the obvious build and the wrong one, as
 * it breaks the things people actually use to enter a code: pasting all six at
 * once, the iOS/Android "from Messages" autofill that `one-time-code` unlocks,
 * and a password manager filling the field. Those all want one field. Keeping
 * one also keeps the caret model simple, so there is no per-box focus juggling
 * to get wrong with backspace or arrow keys.
 */
export function CodeInput({
  value,
  onChange,
  length,
  disabled = false,
  invalid = false,
  autoFocus = false,
  "aria-label": ariaLabel,
}: CodeInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // The box the caret sits in: the first empty one, or the last once full.
  const caretAt = Math.min(value.length, length - 1);

  return (
    <div
      className="relative w-full"
      onPointerDown={(event) => {
        // Any tap anywhere on the row goes to the one real input, so the boxes
        // behave like the single field they actually are.
        event.preventDefault();
        inputRef.current?.focus();
      }}
    >
      <input
        ref={inputRef}
        type="text"
        name="code"
        value={value}
        onChange={(event) =>
          onChange(event.target.value.replace(/\D/g, "").slice(0, length))
        }
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        disabled={disabled}
        maxLength={length}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        // Covers the boxes so taps and the OS autofill affordance land on it,
        // but shows nothing: the boxes below are the visible field.
        className="absolute inset-0 z-10 w-full cursor-default rounded-[12px] border-0 bg-transparent text-transparent caret-transparent outline-none selection:bg-transparent"
      />

      <div className="flex justify-center gap-2 sm:gap-2.5" aria-hidden="true">
        {Array.from({ length }, (_, i) => {
          const char = value[i];
          const isCaret = focused && !disabled && i === caretAt;
          return (
            <span
              key={i}
              className={cn(
                "flex h-14 w-11 items-center justify-center rounded-[12px] sm:w-12",
                "text-[26px] font-light tabular-nums text-white",
                "motion-safe:transition-[background-color,box-shadow] motion-safe:duration-200",
                // Borderless, like the other fields: a filled slab that lifts
                // as it takes a digit, rather than a box that outlines itself.
                char ? "bg-white/[0.12]" : "bg-white/[0.06]",
                isCaret && "bg-white/[0.14] ring-2 ring-white/45",
                invalid && "ring-2 ring-[#ff8a8a]/60",
                disabled && "opacity-50",
              )}
            >
              {char ?? (
                // The empty state is a dot, not a zero: a zero reads as a digit
                // somebody already typed.
                <span className="text-[9px] leading-none text-white/20">
                  &#9679;
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
